// 인게임 좌측 하단 게임 시계("03:25")를 읽는다. OpenCV 없이 순수 JS로 작은 영역만 처리한다.
// (이 시계는 게임 시작 전 대기 시간이 포함된 값이라 "경과 시간"이 아니라, 화면에 보이는 게임 시각을 그대로 보여 주는 용도다.)
//
// 입력은 기준 해상도(높이 1440) 격자의 시계 영역(300×60 RGBA, 화면 왼쪽 아래)이다. 글자는 흰색이 아니라 회색빛이라
// 낮은 임계값으로 분리하고, 앞쪽 숫자 4개(MM SS)만 읽는다(콜론의 점은 작아서 걸러지고, 뒤의 핑 `2ms`는 영역 밖으로 둔다).

export const GAME_TIME_REGION = { x: 0, y: 1380, width: 300, height: 60 }; // 기준 해상도(높이 1440)의 원본 좌표

/**
 * 화면(video) 크기에서 시계 영역을 오려 올 원본 픽셀 위치. HUD는 화면 왼쪽 아래 기준, 크기는 화면 높이 기준(높이 1440 = 배율 1)이다.
 * 오려 온 영역은 GAME_TIME_REGION 크기(300×60)로 맞춰 읽는다.
 */
export function gameTimeRegionRect(videoWidth, videoHeight) {
  const scale = videoHeight / 1440;
  return {
    sx: Math.round(GAME_TIME_REGION.x * scale),
    sy: Math.round(GAME_TIME_REGION.y * scale),
    sw: Math.round(GAME_TIME_REGION.width * scale),
    sh: Math.round(GAME_TIME_REGION.height * scale),
  };
}

const BAND = { y0: 18, y1: 55, xMax: 100 }; // 숫자가 놓이는 영역 안 범위 (뒤의 핑 글자는 x 100 이후)
const THRESHOLD = 125; // 글자로 보는 밝기(회색빛 글자)
const GLYPH_W = 10;
const GLYPH_H = 16;
const MAX_DISTANCE = 0.2;
const MIN_MARGIN = 0.03;

const luma = (data, i) => 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

// 앞쪽 숫자 후보(높이 10~17, 폭 4~11, 픽셀 15 이상)를 왼쪽부터 찾는다. 각 후보는 10×16 비트열을 가진다.
export function extractDigits(image) {
  const { width, data } = image;
  const mask = new Uint8Array(width * image.height);
  for (let y = BAND.y0; y <= BAND.y1; y += 1) {
    for (let x = 0; x < BAND.xMax; x += 1) {
      mask[y * width + x] = luma(data, (y * width + x) * 4) >= THRESHOLD ? 1 : 0;
    }
  }
  const seen = new Uint8Array(mask.length);
  const comps = [];
  for (let y = BAND.y0; y <= BAND.y1; y += 1) {
    for (let x = 0; x < BAND.xMax; x += 1) {
      if (!mask[y * width + x] || seen[y * width + x]) {
        continue; // eslint-disable-line no-continue
      }
      const stack = [[x, y]];
      seen[y * width + x] = 1;
      let [x0, x1, y0, y1, n] = [x, x, y, y, 0];
      while (stack.length) {
        const [cx, cy] = stack.pop();
        n += 1;
        x0 = Math.min(x0, cx);
        x1 = Math.max(x1, cx);
        y0 = Math.min(y0, cy);
        y1 = Math.max(y1, cy);
        [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx >= 0 && nx < BAND.xMax && ny >= BAND.y0 && ny <= BAND.y1 && mask[ny * width + nx] && !seen[ny * width + nx]) {
            seen[ny * width + nx] = 1;
            stack.push([nx, ny]);
          }
        });
      }
      comps.push({ x0, x1, y0, y1, n });
    }
  }
  return comps
    .filter((c) => {
      const w = c.x1 - c.x0 + 1;
      const h = c.y1 - c.y0 + 1;
      return h >= 10 && h <= 17 && w >= 4 && w <= 11 && c.n >= 15;
    })
    .sort((a, b) => a.x0 - b.x0)
    .map((c) => {
      const w = c.x1 - c.x0 + 1;
      const h = c.y1 - c.y0 + 1;
      let bits = '';
      for (let oy = 0; oy < GLYPH_H; oy += 1) {
        for (let ox = 0; ox < GLYPH_W; ox += 1) {
          const sx = c.x0 + Math.min(w - 1, Math.floor(((ox + 0.5) * w) / GLYPH_W));
          const sy = c.y0 + Math.min(h - 1, Math.floor(((oy + 0.5) * h) / GLYPH_H));
          bits += mask[sy * width + sx] ? '1' : '0';
        }
      }
      return bits;
    });
}

const hamming = (a, b) => {
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      diff += 1;
    }
  }
  return diff / a.length;
};

// 숫자 하나를 분류한다. 템플릿은 { 숫자: [비트열(변형)...] }. 못 정하면 null.
export function classifyDigit(bits, templates) {
  const distances = Object.entries(templates)
    .map(([value, variants]) => ({ value, distance: Math.min(...variants.map((v) => hamming(bits, v))) }))
    .sort((a, b) => a.distance - b.distance);
  const [best, second] = distances;
  if (!best || best.distance > MAX_DISTANCE || (second && second.distance - best.distance < MIN_MARGIN)) {
    return null;
  }
  return best.value;
}

/**
 * 시계 영역 이미지 → 게임 시각(초). 앞쪽 숫자 4개(MM SS)를 모두 읽고 초가 59 이하일 때만 값을 돌려준다. 못 읽으면 null.
 */
export function readGameTime(image, templates) {
  const digits = extractDigits(image).slice(0, 4).map((bits) => classifyDigit(bits, templates));
  if (digits.length < 4 || digits.some((d) => d === null)) {
    return null;
  }
  const [m1, m2, s1, s2] = digits.map(Number);
  return s1 > 5 ? null : (m1 * 10 + m2) * 60 + s1 * 10 + s2;
}

// 게임 시각(초) → "MM:SS"
export function formatGameTime(totalSec) {
  if (totalSec === null || totalSec === undefined || totalSec < 0) {
    return null;
  }
  const sec = Math.floor(totalSec);
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
}
